'use strict';

define('vb/private/vx/extension',[
  'vb/private/configLoader',
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/log',
  'vb/private/monitoring/loadMonitorOptions',
  'vb/errors/httpError',
  'vbsw/private/serviceWorkerManager',
], (ConfigLoader, Constants, Utils, Log, LoadMonitorOptions, HttpError, ServiceWorkerManager) => {
  const logger = Log.getLogger('/vb/private/vx/extension', [
    // Register a custom logger
    {
      name: 'greenInfo',
      severity: 'info',
      style: 'green',
    },
  ]);

  /** @typedef {import('vb/private/services/extensionServices').default} ExtensionServices */

  /**
   * The name given to the extension bundle stored in the extension.
   * @type {String}
   */
  const VB_APP_BUNDLE_NAME = 'vb-app-bundle.js';

  /**
   * a regex to find string starting with ui/base or dynamicLayouts/base or translations/base
   * @type {RegExp}
   */
  const baseRegex = new RegExp(`^${Constants.DefaultPaths.UI}base/|^${Constants.DefaultPaths.LAYOUTS}base/|\
^${Constants.DefaultPaths.TRANSLATIONS}base/`);

  /**
   * The regex to find openapi3 files;
   * The () group is used to capture the service name from the path.
   * const serviceRegex = /^services\/self\/([\w_$-]*)\/openapi[\w.]*\.json$/;
   * @type {RegExp}
   */
  const serviceRegex = new RegExp(`^${Constants.DefaultPaths.SERVICES}${Constants.ExtensionFolders.SELF}\
/([\\w_$-]*)/openapi[\\w.]*\\.json$`);

  /**
   * The regular expression to find a catalog in a list of extension files
   * const catalogRegex = /^services\/self\/catalog.json$/;
   * @type {RegExp}
   */
  const catalogRegex = new RegExp(`^${Constants.DefaultPaths.SERVICES}${Constants.ExtensionFolders.SELF}\
/catalog.json$`);

  /**
   * Construct the array of files in this extension given the files in
   * the runtime digest and requirejs-info digest
   * @param {Array<String>} filesFromDef the array of files from the runtime digest
   * @param {Array<String>} bundledResources the array of files from the runtime requirejs-info digest
   * @return {Array<String>}
   */
  const constructFilesArray = (filesFromDef, bundledResources) => {
    const digestFiles = filesFromDef || [];
    const bundleFiles = bundledResources || [];
    const invalidDigestFile = `${Constants.DefaultPaths.LAYOUTS}base/app-flow-x`;
    const shouldDedupe = digestFiles.length > 0 && bundleFiles.length > 0;
    const seen = shouldDedupe ? new Set() : null;
    const result = [];
    let file;
    let i;

    for (i = 0; i < digestFiles.length; i += 1) {
      file = digestFiles[i];
      if (file.indexOf(invalidDigestFile) < 0 && (!seen || !seen.has(file))) {
        if (seen) {
          seen.add(file);
        }
        result.push(file);
      }
    }

    for (i = 0; i < bundleFiles.length; i += 1) {
      file = bundleFiles[i];
      if (file.indexOf(invalidDigestFile) < 0 && (!seen || !seen.has(file))) {
        if (seen) {
          seen.add(file);
        }
        result.push(file);
      }
    }

    return result;
  };

  class Extension {
    constructor(def, appUiInfo, bundleIds, bundledResources, componentsRequirejs, registry) {
      this.id = def.id;

      // the simplified base URL used to defined a requirejs mapping to baseUrlDef
      // this is used so resource can be accessed using a path starting with "vx/ext-id/..."
      this.baseUrl = `${Constants.EXTENSION_PATH}${this.id}/`;

      this.registry = registry;

      this.log = logger;

      /**
       * The list of files in this extension. This is used to determine if a file exist in the extension and to find
       * @type {Array<String>}
       */
      this.files = [];

      // Finish up the initialization
      this._applyDefinition(def, appUiInfo, bundleIds, bundledResources, componentsRequirejs);
    }

    _applyDefinition(def, appUiInfo, bundleIds, bundledResources, componentsRequirejs) {
      this.version = def.version;

      // the baseUrl defined by the extension manager. This is where resources are retrieved from
      // the extension manager
      this.baseUrlDef = def.baseUrl;

      this.files = constructFilesArray(def.files, bundledResources);

      this._initPromise = null; // Initialized in _internalInit()
      this._initDep = null; // Initialized in init()
      /**
       * Resources common to an extension (like functions exposed via $modules) are stored here.
       * @type {Object}
       */
      this.extensionResources = {};

      /** @type {Object.<string, Object>} */
      this.appUiInfo = {};
      const appUis = (appUiInfo && appUiInfo.applications) || [];
      appUis.forEach((appUi) => {
        this.appUiInfo[appUi.id] = appUi;
      });

      // If any App UI is marked offline, mark the extension
      this.offlineEnabled = !!(def.offline || appUis.find((appUi) => appUi.offline));

      /**
       * This is where is stored the components info from the digest.
       * It will not be used until the extension is initialized
       * @type {Object}
       */
      this.componentsRequirejs = componentsRequirejs || {};

      /**
       * Short live array, it will be destroyed once extension is initialized.
       * @type {Array<string>}
       * */
      this._bundleUrls = [];

      /** @type {Array<string>|undefined} */
      this._fileCacheSource = undefined;
      /** @type {number|undefined} */
      this._fileCacheLength = undefined;
      /** @type {Set<string>|undefined} */
      this._fileSetCache = undefined;
      /** @type {Array<Extension>|undefined} */
      this._requiredExtensionsCache = undefined;
      /** @type {Array<Extension>|undefined} */
      this._dependentExtensionsCache = undefined;
      /** @type {string|null|undefined} */
      this._catalogPathCache = undefined;
      /** @type {string|null|undefined} */
      this._bundleUrlCache = undefined;
      /** @type {boolean|undefined} */
      this._extendsBaseArtifactCache = undefined;
      /** @type {{ discoveredServiceMap: Object.<string, string>, catalogPath: string|undefined }|undefined} */
      this._serviceModelDataCache = undefined;

      // When the bundle is declared in the digest, we only need to register
      // the requirejs mapping for it
      if (bundleIds && bundleIds.length > 0) {
        this.log.info('Extension', this.id, 'version:', this.version, 'is using', bundleIds.length, 'bundle(s)');

        // Remember the bundle urls so we can cache them
        this._bundleUrls = this._buildBundleUrlArray(bundleIds);

        // Each extension only needs one mapping to the standard bundles location
        this.registry.addRequireMapping({ [`${this.baseUrl}bundles`]: `${this.baseUrlDef}/bundles` });
        // Remember that this extension is using requirejs bundles so that forcing
        // loading the bundle during init() is not necessary
      }

      this.dependencies = def.dependencies || {};
      this._invalidateDependencyCaches();

      this.extendsHostApp = false;
    }

    _invalidateDependencyCaches() {
      /** @type {boolean|undefined} */
      this.cacheable = undefined;
      this._requiredExtensionsCache = undefined;
      this._dependentExtensionsCache = undefined;
    }

    _ensureFileCachesFresh() {
      if (this._fileCacheSource !== this.files || this._fileCacheLength !== this.files.length) {
        this._fileCacheSource = this.files;
        this._fileCacheLength = this.files.length;
        this._fileSetCache = undefined;
        this._catalogPathCache = undefined;
        this._bundleUrlCache = undefined;
        this._extendsBaseArtifactCache = undefined;
        this._serviceModelDataCache = undefined;
      }
    }

    /**
     * A utility to build an array of bundle urls. This will be used for caching
     * @param {Array<String>} bundleIds
     * @return {Array<String>}
     */
    _buildBundleUrlArray(bundleIds) {
      const baseUrl = Utils.removeTrailingSlash(this.baseUrl);

      return bundleIds.map((bundleId) => {
        const url = bundleId.replace(baseUrl, this.baseUrlDef);
        return `${url}.js`;
      });
    }

    /**
     * Returns the full URL to access an artifact in this extension
     * @return {?String}
     */
    getAbsoluteUrl() {
      // in v2, 'ui/' is in the basePath
      // <baseUrl>/ui/self
      return `${this.baseUrlDef}/${Constants.DefaultPaths.UI}${Constants.ExtensionFolders.SELF}/`;
    }

    /**
     * Given a set, recursively append to this set all the extensions that
     * either depends or are dependent on this extension.
     *
     * The logic to find dependencies is the following:
     *   * add all extensions required and all extensions that extend those and recurse
     *   * all all extensions that extend the current extension and recurse
     *
     * @param {Set}  allDependencies  All dependencies
     */
    getAllDependencies(allDependencies) {
      if (allDependencies.has(this)) {
        return;
      }

      allDependencies.add(this);

      this.getRequiredExtensions().forEach((ext) => {
        this.log.info('Extension', ext.id, 'is a dependency of', this.id);
        if (!allDependencies.has(ext)) {
          allDependencies.add(ext);
          ext.getDependentExtensions().forEach((ext2) => {
            this.log.info('Extension', ext2.id, 'extends', ext.id);
            ext2.getAllDependencies(allDependencies);
          });
        }
      });

      this.getDependentExtensions().forEach((ext2) => {
        this.log.info('Extension', ext2.id, 'extends', this.id);
        ext2.getAllDependencies(allDependencies);
      });
    }

    /**
     * Initialize this extension and all its dependent extensions
     *
     * @return {Promise} a promise that resolve when all related extension are initialized
     */
    init() {
      if (!this._initDep) {
        this._initDep = Promise.resolve().then(() => {
          const allDependencies = new Set();
          this.getAllDependencies(allDependencies);

          // If the extension is marked for offline enabled, cache it.
          return Promise.all([...allDependencies].map((ext) => ext.internalInit()));
        });
      }
      return this._initDep;
    }

    /**
     * Perform the internal tasks needed to initialize this extension
     * @return {Promise} a promise that resolve when all tasks are performed
     */
    internalInit() {
      if (!this._initPromise) {
        this._initPromise = Promise.resolve().then(() => {
          this.log.info('Initializing extension', this.id);
          this._initRequireConfig();
          return this._initBundles()
            .then(() => {
              // Not needed after extension is initialized
              this._bundleUrls = [];
            });
        });
      }

      return this._initPromise;
    }

    /**
     * Set the dependencies of this extension to be cacheable.
     * @private
     */
    setDependenciesCacheable() {
      const dependencies = this.getRequiredExtensions();
      dependencies.forEach((dependency) => {
        if (dependency.cacheable === undefined) {
          // eslint-disable-next-line no-param-reassign
          dependency.cacheable = true;
          // Set this extension's dependencies to be cacheable too.
          dependency.setDependenciesCacheable();
        }
      });
    }

    /**
     * Determine if this extension is cacheable.
     * If this extension or any of its ancestors is offline-enabled AND has a pwaHome appUi, then it is implicitly
     * cacheable.
     * Otherwise if this extension is offline-enabled but some of its dependencies are not, then an error is reported,
     * (and this extension is disabled for caching)
     * @param {String} [extensionIdToSkip] id of extension to skip if checking dependent extensions.
     * @param {boolean} [checkDependencies = true] indicates if we should check dependencies or just this extension and
     * its ancestors.
     * @returns {boolean}
     */
    checkCacheable(extensionIdToSkip, checkDependencies = true) {
      // Have we already determined whether this extension is cacheable or not?
      // If so, do not need to process this extension again.
      const cachedValue = this.cacheable;
      if (cachedValue !== undefined) {
        return cachedValue;
      }

      // We must be checking extensionIdToSkip
      if (extensionIdToSkip === this.id) {
        return false;
      }

      if (!Extension.isCachingEnabled()) {
        this.cacheable = false;
        return false;
      }

      // If this extension is not offline-enabled, see if any extension that depends on it is cacheable.
      // That would make this extension implicitly cacheable.
      if (!this.offlineEnabled) {
        // Check dependent extensions, but do not bother checking all those extensions' dependencies.
        /** @type {Array<Extension>} */
        const dependents = this.registry.getExtensionsDependingOn(this.id);
        this.cacheable = dependents.reduce(
          (cacheable, dependent) => cacheable
            || dependent.checkCacheable(extensionIdToSkip, false /* !checkDependencies */),
          false, // assume cacheable = false until proven otherwise
        );

        if (this.cacheable) {
          this.log.info('Extension', this.id, 'is not offline-enabled but is required by a cacheable extension.',
            'Making this extension cacheable.');

          // Set this extension's dependencies to be cacheable
          this.setDependenciesCacheable();
        }
        return this.cacheable;
      }

      //
      // This extension is offline-enabled. Now we check if it is cacheable.
      // If checkDependencies is false, we just want to see if this extension has an appUi marked as pwaHome.
      // (A child extension is checking its dependentExtensions, so we do not need to check into this
      // extension's dependencies).
      //

      // If any AppUi in this extension is marked as a pwaHome, then make sure we cache it.
      // (By setting this, the appUi is asserting that all required data for offline is handled)
      /** @type {Object.<string, Object>} */
      const appUiInfo = this.appUiInfo || {};
      const hasPwaHome = Object.values(appUiInfo).find((appUi) => !!appUi.pwaHome);
      if (hasPwaHome) {
        this.cacheable = true;

        // Set this extension's dependencies to be cacheable
        this.setDependenciesCacheable();
        return true;
      }

      if (checkDependencies) {
        // Otherwise, ensure that all of its dependencies are offline-enabled as well and write an error otherwise.
        const dependencies = this.getRequiredExtensions();
        this.cacheable = dependencies.reduce(
          (cacheable, dependency) => {
            // Check each dependency (don't short circuit if cacheable is already set to false), so that we get
            // messages that indicate all the dependencies that cause this extension to be not cacheable.
            if (!dependency.checkCacheable(this.id)) {
              this.log.error('Extension', this.id, 'is offline-enabled but depends on extension', dependency.id,
                'which is not. Disabling offline for', this.id);
              return false;
            }
            return cacheable;
          },
          true, // To begin, assume cacheable = true
        );
        return this.cacheable;
      }

      return false;
    }

    /**
     * Initialize the extension bundle given the bundle information in the manifest coming from
     * the requirejs-info digest.
     * When bundles definition is present in the requirejs-info digest only register those bundle URLs
     * with the cache. This is the most likely case.
     *
     * If no bundle information is given, find bundles in file list and force its loading. This should not
     * happen for a property bundle extension.
     *
     * @return {Promise} a promise that resolve when the bundle is initialized
     */
    async _initBundles() {
      const cacheExtension = this.checkCacheable();
      let promise;
      // If no bundle info is given, force the loading using the bundle name
      if (this._bundleUrls.length === 0) {
        // If a bundle is defined in the list of files, use it.
        const bundleUrl = this._findBundleUrl();

        if (cacheExtension) {
          // Cache the extension for pwa, but we don't need to wait for its promise to resolve.
          this._cacheExtension(bundleUrl ? [bundleUrl] : undefined);
        }

        // Either load the extension bundle or create a require mapping vx/extId/
        if (bundleUrl) {
          promise = this._loadBundle(bundleUrl);
        } else {
          this.log.info('Applying extension', this.id, 'version:', this.version, 'without bundle');
          this.registry.addRequireMapping(this.buildMapping());
        }
      } else if (cacheExtension) {
        // Cache the extension for pwa, but we don't need to wait for its promise to resolve.
        this._cacheExtension(this._bundleUrls);
      }

      return promise;
    }

    /**
     * Caching is enabled if it is a WebApp PWA and vbInitConfig.PWA_CONFIG.disableCaching is NOT set to true
     * and vbInitConfig.PWA_CONFIG.disableExtensionCaching is NOT set to true
     * @returns {boolean}
     */
    static isCachingEnabled() {
      const swMgrInstance = ServiceWorkerManager.getInstance();
      return swMgrInstance.isExtensionCachingEnabled();
    }

    /**
     * Cache the Extension's bundleUrls if supplied, or its files.
     * @param {Array<string>} [bundleUrls] if caching bundles rather than the extension's files
     * @protected
     */
    _cacheExtension(bundleUrls) {
      this.log.info('Initializing Cache for extension', this.id, 'version:', this.version);

      const swMgrInstance = ServiceWorkerManager.getInstance();
      swMgrInstance.setupExtensionCache(() => {
        let resources;
        if (bundleUrls) {
          // Copy bundleUrls, because we are going to add to it
          resources = bundleUrls.slice();
          bundleUrls.forEach((bundleUrl) => {
            // cache the bundle's .map file too
            const bundleMapPath = `${bundleUrl.substring(this.baseUrlDef.length + 1)}.map`;
            if (this.hasFile(bundleMapPath)) {
              resources.push(`${this.baseUrlDef}/${bundleMapPath}`);
            }
          });
        } else {
          // No bundles?  Get the list of resources to cache.
          // Let service worker do the concatenation of path + resource
          resources = this.files;
        }

        return {
          id: this.id,
          version: this.version,
          baseUrl: this.baseUrlDef,
          resources,
        };
      })
        .catch((error) => {
          // Log error and continue
          this.log.error('Failed to initialize cache for extension', this.id, 'version:', this.version, error);
        });
    }

    /**
     * Initializes the require configuration from the requirejs-info digest.
     */
    _initRequireConfig() {
      ConfigLoader.addRequirejsPaths(this.componentsRequirejs, this.baseUrl, this.id);
      // Since the registration is done, remove the declaration from the componentsRequirejs
      this.componentsRequirejs = {};
    }

    /**
     * Retrieve all the extensions that this extension depends on.
     * @return {Array<Extension>}
     */
    getRequiredExtensions() {
      if (!this._requiredExtensionsCache) {
        const result = [];
        Object.keys(this.dependencies).forEach((extId) => {
          const ext = this.registry.getExtensionById(extId);
          if (ext) {
            result.push(ext);
          } else {
            this.log.warn('Extension', this.id, 'depends on extension', extId, 'that does not exist.');
          }
        });

        this._requiredExtensionsCache = result;
      }

      return this._requiredExtensionsCache;
    }

    /**
     * Return all the extensions extending this extension
     * @return {Array<Extension>}
     * @private
     */
    getDependentExtensions() {
      if (!this._dependentExtensionsCache) {
        const uiPrefix = `${Constants.DefaultPaths.UI}${this.id}/`;
        const layoutPrefix = `${Constants.DefaultPaths.LAYOUTS}${this.id}/`;
        const translationsPrefix = `${Constants.DefaultPaths.TRANSLATIONS}${this.id}/`;
        /** @type {Array<Extension>} */
        const dependentExtensions = this.registry.getExtensionsDependingOn(this.id);
        this._dependentExtensionsCache = dependentExtensions
          .filter((ext) => ext.files.some((file) => file.startsWith(uiPrefix)
            || file.startsWith(layoutPrefix)
            || file.startsWith(translationsPrefix)));
      }

      return this._dependentExtensionsCache || [];
    }

    /**
     * return true when at least one of the file extends a base object
     * @return {Boolean}
     */
    extendsBaseArtifact() {
      this._ensureFileCachesFresh();
      if (this._extendsBaseArtifactCache === undefined) {
        this._extendsBaseArtifactCache = this.files.some((file) => baseRegex.test(file));
      }
      return this._extendsBaseArtifactCache;
    }

    /**
     * Verify the validity of the extension metadata returned by the extension manager
     * @return {Boolean}
     */
    isValid() {
      return (this.baseUrlDef && this.id && Array.isArray(this.files) && this.files.length > 0);
    }

    /**
     * Return true if a file path exist in the array of files
     * @param  {String} path
     * @return {Boolean}
     */
    hasFile(path) {
      this._ensureFileCachesFresh();
      if (!this._fileSetCache) {
        this._fileSetCache = new Set(this.files);
      }
      return this._fileSetCache.has(path);
    }

    /**
     * Check if a resource file exist in the extension
     * @param  {String} path
     * @return {Boolean}
     * @throws {HttpError} throw a 404 HttpError if the file doesn't exist in the extension
     */
    fileExists(path) {
      if (this.hasFile(path)) {
        return true;
      }
      throw new HttpError(404, null, `${path} not found.`);
    }

    /**
     * Creates an ExtensionServices Object for the application extension, and creates a name/file map
     * from the contents of the extension.
     *
     * @param {Object} options the standard options used to construct a Services object.
     * @param {module} ServicesModel ExtensionServices need to be loaded later than this module because
     *                              it forces JET to load before ojL10n is setup in ConfigLoader
     *
     * @returns {ExtensionServices} new instance of the ServicesModel
     */
    loadServicesModel(options, ServicesModel) {
      this._ensureFileCachesFresh();
      if (!this._serviceModelDataCache) {
        /** @type {Object.<string, string>} */
        const discoveredServiceMap = {};

        this.files.forEach((file) => {
          const match = file.match(serviceRegex);
          if (match) {
            const [path, name] = match;
            discoveredServiceMap[name] = path;
          }
        });

        this._serviceModelDataCache = {
          discoveredServiceMap,
          catalogPath: this._findCatalog(),
        };
      }

      const serviceModelData = this._serviceModelDataCache || {
        discoveredServiceMap: {},
        catalogPath: undefined,
      };
      const { discoveredServiceMap, catalogPath } = serviceModelData;

      // populated with the services we can find on the extension manifest
      /** @type {Object.<string, string>} */
      const extensionServiceMap = {};
      const declaredServiceMap = options && options.serviceFileMap;
      Object.entries(discoveredServiceMap).forEach(([name, path]) => {
        // we need to check if the extension has an explicit serviceFileMap declaration to ensure we are
        // not replacing it
        const declaredPath = declaredServiceMap && declaredServiceMap[name];
        if (!declaredPath) {
          extensionServiceMap[name] = path;
        } else if (declaredPath !== path) {
          // it would be weird to declare a path for a service that doesn't match its name,
          // if one that did match its name already existed.
          this.log.warn('Extension', this.id, 'contains service metadata ', path,
            '. The declared file will be used instead:', declaredPath);
        }
      });

      const optionsClone = Object.assign({ extensionServiceMap }, options);

      if (catalogPath) {
        optionsClone.extensions = optionsClone.extensions || {};
        optionsClone.extensions.catalogPaths = {
          [this.id]: catalogPath,
        };
      }

      return new ServicesModel(optionsClone);
    }

    /**
     * if there is a catalog.json in self/, returns the vx-mapped path to the file
     * ex:  vx/ext2/self/services/catalog.json
     * @returns {string|undefined}
     * @private
     */
    _findCatalog() {
      this._ensureFileCachesFresh();
      if (this._catalogPathCache === undefined) {
        const catalogFile = this.files.find((file) => catalogRegex.test(file));
        this._catalogPathCache = catalogFile ? `${Constants.EXTENSION_PATH}${this.id}/${catalogFile}` : null;
      }
      return this._catalogPathCache || undefined;
    }

    /**
     * Retrieve the URL of the bundle if there is one defined in the extension
     * @return {string|undefined} the bundle URL or undefined
     * @private
     */
    _findBundleUrl() {
      this._ensureFileCachesFresh();
      if (this._bundleUrlCache === undefined) {
        const bundlePath = this.files.find((path) => path.indexOf(VB_APP_BUNDLE_NAME) > 0);
        if (bundlePath) {
          let bundleUrl = `${this.baseUrlDef}/${bundlePath}`;
          bundleUrl = globalThis.requirejs.toUrl(bundleUrl);
          this._bundleUrlCache = bundleUrl;
        } else {
          this._bundleUrlCache = null;
        }
      }

      return this._bundleUrlCache || undefined;
    }

    /**
     * Load the extension bundle. This is only needed for not bundled extension.
     *
     * @param  {String} bundleUrl
     * @return {Promise} a promise that resolve when the bundle is loaded
     */
    _loadBundle(bundleUrl) {
      const mo = new LoadMonitorOptions(
        LoadMonitorOptions.SPAN_NAMES.LOAD_EXTENSION_BUNDLE, `Extension ${this.id} bundle load`, this,
      );
      return this.log.monitor(mo, (extensionLoadTimer) => Utils.getResource(bundleUrl)
        .then(() => {
          this.log.greenInfo(
            'Extension', this.id, 'version:', this.version, 'is forcing a bundle load', extensionLoadTimer(),
          );
        })
        .catch((error) => {
          extensionLoadTimer(error);
          throw error;
        }));
    }

    /**
     * Given an App UI id, return the info for it
     * @param  {String} id an App UI id
     * @return {Object} the appUiInfo
     */
    getAppUiInfo(id) {
      return this.appUiInfo[id] || { id };
    }

    /**
     * Given an extension id and its base URL this function returns a mapping (object with properties/values)
     * to be used with requirejs like:
     * {
     *   'vx/ext-id': 'file://.../sources'
     * }
     * @return {Object} a mapping string
     */
    buildMapping() {
      return {
        [Utils.removeTrailingSlash(this.baseUrl)]: this.baseUrlDef,
      };
    }
  }

  return Extension;
});

